import { atom } from 'jotai';

import { WINDOW_COUNT, WINDOW_STORAGE_PREFIX, safeStorage, type ViewerWindowId } from '@/lib/floatingWindow';

/**
 * Shared state for the viewer's movable windows (spec §12.1).
 *
 * **z-order (ruling D27).** There is deliberately no "highest z so far"
 * counter: one that only ever increments eventually climbs over the modals and
 * toasts the windows must always stay under. Instead the windows rotate inside
 * a fixed band — `WINDOW_BASE_Z` (21) for an untouched window and
 * `[22 … WINDOW_MAX_Z]` (24) for the ones the user has interacted with, front-
 * most highest. `floatingWindowStackAtom` holds that order, so bringing one
 * window forward pushes the others down a slot in the same render; a window's
 * z is derived from the stack rather than remembered locally, which is what
 * keeps two windows from ending up on the same slot.
 */
export const floatingWindowStackAtom = atom<ViewerWindowId[]>([]);

/**
 * Claim the front. Every window derives its own z from `floatingWindowStackAtom`
 * (`FloatingWindow` calls `windowZIndex`), so this writes the order and returns
 * nothing — a returned z would be a second source for the same number.
 *
 * The `id` is **required** (ruling D42): an optional one made a bare
 * `bringToFront()` compile, type-check and silently do nothing, which is a
 * bring-to-front that never brings anything forward. A missing id is now a
 * compile error.
 */
export const bringWindowToFrontAtom = atom(null, (get, set, id: ViewerWindowId): void => {
  const stack = get(floatingWindowStackAtom);
  if (stack[stack.length - 1] === id) return;
  // Keep at most one slot per window; a window dropping off the back simply
  // returns to the base of the band.
  set(floatingWindowStackAtom, [...stack.filter((w) => w !== id), id].slice(-WINDOW_COUNT));
});

/**
 * The corner minimap is expanded (`MapView`'s own Expand control).
 *
 * `MapView` owns the state, but the window that POSITIONS it is its parent
 * (`MinimapWindow`), so the flag travels by atom rather than by a prop that
 * would have to climb up through `Viewer` and back down. While it is set the
 * window holds its anchor (`FloatingWindow.atAnchor`, ruling D57): the
 * expanded card is sized to the whole canvas region, and a card that size
 * placed at a dragged offset hangs off the edge of `#canvas-container`.
 */
export const minimapExpandedAtom = atom(false);

/**
 * Bumped by "Reset layout". Every mounted `FloatingWindow` depends on it, so
 * clearing localStorage alone is not enough — this is what makes them all
 * re-read (now empty) storage in the same frame.
 */
export const floatingWindowResetAtom = atom(0);

/** Forget every stored window position. Safe when storage is unavailable. */
export function clearFloatingWindowPositions(): void {
  for (const key of safeStorage.keys()) {
    if (key.startsWith(WINDOW_STORAGE_PREFIX)) safeStorage.remove(key);
  }
}
